import React, { useState, useEffect } from "react";
import { getRequest, postRequest, patchRequest } from "../../axiosRoutes/index";
import {
  Button,
  Modal,
  Box,
  Typography,
  OutlinedInput,
  FormControl,
  InputLabel,
  Grid,
  MenuItem,
  Select,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { useSnackbar } from "../../axiosRoutes/snackbarConfig";
import dayjs from "dayjs";

const currentYear = dayjs().year();
const minDate = `${currentYear}-01-01`;
const maxDate = `${currentYear+2}-12-31`;

const AddLeave = ({ open, handleClose, leaveId }) => {
  const { snackbar, showSnackbar, handleSnackbarClose } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [leaveType, setLeaveType] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [leaveData, setLeaveData] = useState({
    userId: "",
    type: "",
    reason: "",
    status: "",
    comments: "",
    startDate: "",
    endDate: "",
  });

  const fetchLeaveType = async () => {
    try {
      const response = await getRequest("filter/leave");
      const statusArray = Object.keys(response).map((key) => ({
        label: key,
        value: response[key],
      }));
      setLeaveType(statusArray);
    } catch (error) {
      console.error("Error fetching type:", error);
    }
  };

  const fetchStatus = async () => {
    try {
      const response = await getRequest("filter/status");
      const statusArray = Object.keys(response).map((key) => ({
        label: key,
        value: response[key],
      }));
      setStatuses(statusArray);
    } catch (error) {
      console.error("Error fetching Filter:", error);
    }
  };

  useEffect(() => {
    fetchStatus();
    fetchLeaveType();
  }, []);

  useEffect(() => {
    const fetchEmployees = async () => {
      const access = localStorage.getItem("access");
      try {
        const response = await getRequest("employee/getAllEmployees", {
          headers: {
            Authorization: `Bearer ${access}`,
          },
        });
        const activeEmployees = response.filter(emp => emp.status === "active");
        setEmployees(activeEmployees);
      } catch (error) {
        console.error("Error fetching Employees:", error);
      }
    };
    fetchEmployees();
  }, []);

  useEffect(() => {
    const fetchLeave = async () => {
      if (leaveId !== null) {
        const access = localStorage.getItem("access");
        try {
          setLoading(true);
          const response = await getRequest(`admin/leave/${leaveId}`, {
            headers: {
              Authorization: `Bearer ${access}`,
            },
          });
          const formatStartDate = new Date(response.startDate)
            .toISOString()
            .split("T")[0];
          const formatEndDate = new Date(response.endDate)
            .toISOString()
            .split("T")[0];
          setLeaveData({
            userId: response.userId,
            reason: response.reason,
            status: response.status,
            type: response.type,
            comments: response.comments,
            startDate: formatStartDate,
            endDate: formatEndDate,
          });
        } catch (error) {
          console.error("Error fetching Leave:", error);
        } finally {
          setLoading(false);
        }
      } else {
        setLeaveData({
          userId: "",
          type: "",
          startDate: "",
          endDate: "",
          comments: "",
          reason: "",
          status: "",
        });
      }
    };
    fetchLeave();
  }, [leaveId]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const access = localStorage.getItem("access");
      await patchRequest(
        `admin/leave/${leaveId}`,
        {
          Authorization: `Bearer ${access}`,
        },
        leaveData
      );
      showSnackbar("Leave Updated Successfully", "success");
      handleClose();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || "An error occurred";
      showSnackbar(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLeaveData({ ...leaveData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const access = localStorage.getItem("access");
      await postRequest(
        "admin/leave",
        {
          Authorization: `Bearer ${access}`,
        },
        leaveData
      );
      showSnackbar("Leave Applied Successfully", "success");
      setLeaveData({
        userId: "",
        type: "",
        startDate: "",
        comments: "",
        endDate: "",
        reason: "",
        status: "",
      });
      handleClose();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || "An error occurred";
      showSnackbar(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        open={open}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: '80%', md: '35%' },
            maxHeight: "80vh",
            overflowY: "auto",
            bgcolor: "black",
            color: "#FEFEFE",
            border: "1px solid #A9A9A9",
            p: 2,
            "& .MuiOutlinedInput-root": {
              color: "#FEFEFE",
              "& fieldset": {
                borderColor: "#FEFEFE",
              },
              "&:hover fieldset": {
                borderColor: "#FEFEFE",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#FEFEFE",
              },
            },
            "& .MuiInputLabel-root": {
              color: "#FEFEFE",
            },
            "& .MuiIconButton-root": {
              color: "#FEFEFE",
            },
          }}
        >
          <Typography
            id="modal-modal-title"
            variant="h4"
            fontWeight="bold"
            textAlign="center"
            component="h1"
            sx={{ mb: 2 }}
          >
            {leaveId ? "Update Leave Application" : "Add Leave Application"}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" fullWidth sx={{ mb: 1 }}>
                <InputLabel htmlFor="outlined-adornment-employee">
                  Employee
                </InputLabel>
                <Select
                  labelId="outlined-adornment-employee-label"
                  id="outlined-adornment-employee"
                  name="userId"
                  label="Employee"
                  readOnly={leaveId ? true : false}
                  value={leaveData.userId}
                  onChange={handleChange}
                  sx={{ backgroundColor: "transparent" }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                        overflowY: "auto",
                      },
                    },
                  }}
                >
                  {employees ? employees.map((employee) => (
                    <MenuItem
                      key={employee.userId._id}
                      value={employee.userId._id}
                    >
                      {employee.userId.name}
                    </MenuItem>
                  )) : <MenuItem disabled={true}>No Data yet</MenuItem>}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" fullWidth sx={{ mb: 1 }}>
                <InputLabel htmlFor="outlined-adornment-type">
                  Leave Type
                </InputLabel>
                <Select
                  labelId="outlined-adornment-type-label"
                  id="outlined-adornment-type"
                  name="type"
                  label="Leave Type"
                  value={leaveData.type}
                  onChange={handleChange}
                  sx={{ backgroundColor: "transparent" }}
                >
                  {leaveType.map((leaveOpt) => (
                    <MenuItem key={leaveOpt.value} value={leaveOpt.value}>
                      {leaveOpt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" fullWidth sx={{ mb: 1 }}>
                <InputLabel htmlFor="outlined-adornment-reason">
                  Reason
                </InputLabel>
                <OutlinedInput
                  id="outlined-adornment-reason"
                  type="text"
                  name="reason"
                  label="reason"
                  value={leaveData.reason}
                  onChange={handleChange}
                  sx={{ backgroundColor: "transparent" }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" fullWidth sx={{ mb: 1 }}>
                <InputLabel htmlFor="outlined-adornment-status">
                  Status
                </InputLabel>
                <Select
                  labelId="outlined-adornment-status-label"
                  id="outlined-adornment-status"
                  name="status"
                  label="Status"
                  value={leaveData.status}
                  onChange={handleChange}
                  sx={{ backgroundColor: "transparent" }}
                >
                  {statuses.map((statusOpt) => (
                    <MenuItem key={statusOpt.value} value={statusOpt.value}>
                      {statusOpt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={12}>
              <FormControl variant="outlined" fullWidth sx={{ mb: 1 }}>
                <InputLabel htmlFor="outlined-adornment-Comments">
                  Comments
                </InputLabel>
                <OutlinedInput
                  id="outlined-adornment-comments"
                  type="text"
                  name="comments"
                  label="Comments"
                  value={leaveData.comments}
                  onChange={handleChange}
                  sx={{ backgroundColor: "transparent" }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" fullWidth sx={{ mb: 1 }}>
                <InputLabel
                  htmlFor="outlined-adornment-startDate"
                  shrink
                  sx={{
                    backgroundColor: "#000",
                    paddingX: "4px",
                    marginLeft: "-4px",
                  }}
                >
                  Start Date
                </InputLabel>
                <OutlinedInput
                  id="outlined-adornment-startDate"
                  type="date"
                  name="startDate"
                  label="Start Date"
                  value={leaveData.startDate}
                  onChange={handleChange}
                  inputProps={{ min: minDate, max: maxDate }}
                  sx={{ backgroundColor: "transparent" }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" fullWidth sx={{ mb: 1 }}>
                <InputLabel
                  htmlFor="outlined-adornment-endDate"
                  shrink
                  sx={{
                    backgroundColor: "#000",
                    paddingX: "4px",
                    marginLeft: "-4px",
                  }}
                >
                  End Date
                </InputLabel>
                <OutlinedInput
                  id="outlined-adornment-endDate"
                  type="date"
                  name="endDate"
                  label="End Date"
                  value={leaveData.endDate}
                  onChange={handleChange}
                  inputProps={{ min: minDate, max: maxDate }}
                  sx={{ backgroundColor: "transparent" }}
                />
              </FormControl>
            </Grid>
            {leaveId !== null ? (
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleUpdate}
                  sx={{
                    bgcolor: "#E5622D",
                    "&:hover": { bgcolor: "#D0521B" },
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "#FF5722" }} />
                  ) : (
                    "Update"
                  )}
                </Button>
              </Grid>
            ) : (
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSubmit}
                  sx={{
                    bgcolor: "#E5622D",
                    "&:hover": { bgcolor: "#D0521B" },
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "#FF5722" }} />
                  ) : (
                    "Add"
                  )}
                </Button>
              </Grid>
            )}
            <Grid item xs={6}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleClose}
                disabled={loading}
                sx={{ bgcolor: "#A9A9A9", "&:hover": { bgcolor: "#E1E1E1" } }}
              >
                Cancel
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddLeave;
